/**
 * Resolves the navigation target for a conversation's standalone page.
 *
 * @param conversationId - The conversation to build a route for.
 * @param lookup - Resolves the conversation's owner, or null when the
 *   conversation is unavailable.
 * @returns The route descriptor, or null when the conversation is unavailable.
 */
export async function resolveAgentConversationRoute(
	conversationId: string,
	lookup: () => Promise<{ ownerId: string } | null>,
) {
	const conversation = await lookup();
	return conversation ?
			({
				to: '/user/$ownerId/conversations/$conversationId',
				params: { ownerId: conversation.ownerId, conversationId },
			} as const)
		:	null;
}

interface CurrentUserOwnerDirectory {
	actor: { userId: string; displayName: string };
	memberships: Array<{ groupId: string; groupName: string }>;
}

/**
 * Resolves a human-readable display name for a conversation owner. The owner is
 * either the current user or one of their group memberships.
 *
 * @param ownerId - The owner whose name to resolve.
 * @param currentUser - The current user's actor and memberships, if loaded.
 * @returns The owner's display name, falling back to `ownerId` when no non-empty
 *   name is known.
 */
export function resolveAgentConversationOwnerName(
	ownerId: string,
	currentUser: CurrentUserOwnerDirectory | null | undefined,
): string {
	if (currentUser?.actor.userId === ownerId) {
		const displayName = currentUser.actor.displayName.trim();
		return displayName === '' ? ownerId : displayName;
	}

	const groupName =
		currentUser?.memberships.find((membership) => membership.groupId === ownerId)?.groupName.trim() ?? '';
	return groupName === '' ? ownerId : groupName;
}
